import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowUp, Bell, Briefcase, Plane, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { yasminApi } from "@/lib/yasminApi";
import { SITE, cityBySlug, usePageMeta } from "@/lib/site";
import { PageHeader, Section, InfoCard, Prose, FlightSearch, OutLink, CityTile } from "@/components/site/SiteBits";
import { NotFound } from "@/components/SiteLinks";
import { Button } from "@/components/ui/button";
import { travelApi } from "@/lib/travelApi";
import { ThingsToDo, PlaceCard } from "@/pages/travel/TripPlanner";
import { AgentBrief } from "@/components/agents/Agents";
import { TravelExtras } from "@/components/site/TravelExtras";
import { TripPrepCard } from "@/components/site/TripPrepCard";

const airalo = (path = "") => `https://www.airalo.com/${path}`;

export function TravelPage() {
  usePageMeta("Travel hub — flights, eSIMs and city guides", "Search flights from UK airports, get a travel eSIM and read our city guides to Barcelona, Paris, Amsterdam, Rome, Lisbon and more.");
  return (
    <div>
      <PageHeader kicker="Travel" title="Plan the trip, not just the booking" lede="Live fares to any airport in the world, data for your phone and things to do when you land, with guides to the cities people travel to most from the UK.">
        <div className="flex flex-wrap gap-3">
          <Link to="/travel/plan"><Button className="rounded-full px-6" data-testid="travel-plan-cta">Plan a trip anywhere</Button></Link>
          <Link to="/yasmin/chat"><Button variant="outline" className="rounded-full px-6" data-testid="travel-yasmin-cta"><Sparkles className="mr-2 h-4 w-4" />Ask Yasmin</Button></Link>
          <Link to="/travel/trips"><Button variant="ghost" className="rounded-full px-6" data-testid="travel-trips-cta">My trips</Button></Link>
        </div>
      </PageHeader>
      <YasminTravel />
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6"><AgentBrief agent="kavaya" /></div>
      <TravelExtras />
      <FeaturedPlaces />
      <Section title="Search flights" lede="Pick your airport, destination and dates. Signed in, you see live fares from Duffel; either way we hand you to a partner with the search ready, cheapest first." id="flights">
        <div className="max-w-2xl"><FlightSearch /></div>
      </Section>
      <Section alt id="guides" title="City guides" lede={`Getting in from the airport, where to stay, what to book ahead and local tips. Reviewed ${SITE.reviewed}.`}>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {SITE.cities.map((c) => <CityTile key={c.slug} c={c} />)}
        </div>
      </Section>
      <Section>
        <div className="grid gap-4 md:grid-cols-2">
          <InfoCard title="Data abroad with an eSIM">
            <p>Skip roaming charges: install a travel eSIM before you fly and switch it on when you land. Most recent phones support eSIMs.</p>
            <Link to="/travel/esims" className="mt-3 inline-block font-semibold text-primary hover:underline">eSIM guide →</Link>
          </InfoCard>
          <InfoCard title="Things to do, anywhere">
            <p>Tours, attractions, passes and airport transfers in any city on earth — GetYourGuide, Viator, Klook, Tiqets and Civitatis, pre-filled with your destination.</p>
            <Link to="/travel/plan" className="mt-3 inline-block font-semibold text-primary hover:underline">Pick a city →</Link>
          </InfoCard>
        </div>
      </Section>
    </div>
  );
}

export function EsimsPage() {
  usePageMeta("Travel eSIMs explained", "How travel eSIMs work, how to check your phone and how to set one up before your trip.");
  return (
    <div>
      <PageHeader kicker="Travel" title="Travel eSIMs, explained" lede="An eSIM is a digital SIM you download to your phone. Use it for data abroad and keep your usual number for calls and texts." />
      <Section>
        <div className="grid items-start gap-10 lg:grid-cols-[1fr_340px]">
          <Prose>
            <h2>Why use one</h2>
            <p>Roaming policies on UK networks vary, and daily roaming charges add up quickly on a week away. A travel eSIM gives you a fixed amount of data for a fixed price, bought before you go.</p>
            <h2>Check your phone first</h2>
            <p>Your phone must support eSIM and be unlocked. Most iPhones from the XS/XR onwards and many recent Android phones do. On iPhone, look under Settings → General → About for "Digital SIM" or "Available SIM".</p>
            <h2>How to set it up</h2>
            <ol>
              <li>Buy a plan for your destination before you travel.</li>
              <li>Install it over Wi-Fi at home by scanning the QR code or following the in-app steps.</li>
              <li>Leave it switched off until you land, then turn it on and enable data roaming for the eSIM line only.</li>
              <li>Keep your main SIM on for calls and texts, or turn its data off to avoid charges.</li>
            </ol>
            <h2>Tips</h2>
            <ul>
              <li>Pick a regional plan if you're visiting several countries.</li>
              <li>Hotspotting works with most plans, but check the plan details.</li>
              <li>Delete the eSIM once you're home and the plan has expired.</li>
            </ul>
          </Prose>
          <InfoCard title="eSIMs for our guide cities">
            <ul className="mt-2 space-y-2">
              {SITE.cities.map((c) => (
                <li key={c.slug}><OutLink href={airalo(c.esim)}>{c.name} — {c.country} eSIM</OutLink></li>
              ))}
            </ul>
            <p className="mt-4 text-xs">Links go to Airalo, our eSIM partner. If you buy, we may earn a commission at no extra cost to you.</p>
          </InfoCard>
        </div>
      </Section>
    </div>
  );
}

const IDEAS = ["Plan 3 days in Lisbon under £600", "A weekend in Paris for two", "Cheapest flights to Barcelona next month", "Family trip to Rome in the summer"];

// Yasmin inside Travel: one message becomes flights, a hotel and the bookings around it. Signed-in travellers also
// see their next trip, the plans Yasmin made and their flight price alerts.
function YasminTravel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [trips, setTrips] = useState([]);
  const [plans, setPlans] = useState([]);
  const [watches, setWatches] = useState([]);
  useEffect(() => {
    if (!user) return;
    travelApi.trips().then((r) => setTrips(r?.upcoming || [])).catch(() => {});
    yasminApi.plans().then((r) => setPlans((r?.plans || []).filter((p) => !p.past))).catch(() => {});
    yasminApi.watches().then((r) => setWatches((r?.watches || []).filter((w) => w.status === "watching"))).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const ask = (q) => {
    const m = (q ?? text).trim();
    navigate(m ? "/yasmin/chat?q=" + encodeURIComponent(m) : "/yasmin/chat");
  };
  const next = trips[0];
  return (
    <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6" data-testid="travel-yasmin">
      <div className="relative overflow-hidden rounded-3xl p-6 text-white sm:p-8" style={{ background: "linear-gradient(135deg,#023E8A 0%,#0077B6 55%,#00B4D8 100%)" }}>
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/20"><Sparkles className="h-5 w-5" /></span>
          <div>
            <p className="text-xl font-bold sm:text-2xl">{user?.full_name ? "Where next, " + user.full_name.trim().split(/\s+/)[0] + "?" : "Where next?"}</p>
            <p className="text-sm text-white/85">Tell Yasmin the trip. She lines up flights, stays and things to do. Nothing is booked until you confirm.</p>
          </div>
        </div>
        <form className="mt-5 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. 4 days in Lisbon in May" aria-label="Ask Yasmin to plan a trip" data-testid="travel-ask"
            className="h-12 min-w-0 flex-1 rounded-full bg-white/20 px-5 text-white placeholder:text-white/70 focus:outline-none focus:ring-2 focus:ring-white/60" />
          <button type="submit" aria-label="Send to Yasmin" data-testid="travel-ask-send" className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#0077B6]"><ArrowUp className="h-5 w-5" /></button>
        </form>
        <div className="mt-3 flex flex-wrap gap-2">
          {IDEAS.map((i, n) => <button key={i} type="button" onClick={() => ask(i)} data-testid={"travel-idea-" + n} className="rounded-full bg-white/20 px-3 py-1.5 text-xs font-semibold hover:bg-white/30">{i}</button>)}
        </div>
      </div>
      {user && (next || plans.length || watches.length) ? (
        <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="travel-mine">
          {next ? (
            <Link to={"/travel/trips/" + next.id} className="flex items-center gap-3 rounded-2xl border bg-card p-4 hover:bg-muted/50" data-testid="travel-next-trip">
              <Plane className="h-5 w-5 text-primary" />
              <div className="min-w-0"><p className="text-xs font-semibold text-primary">NEXT TRIP</p><p className="truncate font-semibold">{next.name}</p><p className="truncate text-xs text-muted-foreground">{next.depart_date}{next.return_date ? " – " + next.return_date : ""}</p></div>
            </Link>
          ) : null}
          {plans.length ? (
            <Link to={plans.length === 1 ? "/travel/plans/" + plans[0].id : "/travel/trips"} className="flex items-center gap-3 rounded-2xl border bg-card p-4 hover:bg-muted/50" data-testid="travel-plans">
              <Briefcase className="h-5 w-5 text-primary" />
              <div className="min-w-0"><p className="text-xs font-semibold text-primary">PLANNED WITH YASMIN</p><p className="truncate font-semibold">{plans[0].title}</p><p className="truncate text-xs text-muted-foreground">{plans.length} plan{plans.length === 1 ? "" : "s"}</p></div>
            </Link>
          ) : null}
          {watches.length ? (
            <Link to="/travel/trips" className="flex items-center gap-3 rounded-2xl border bg-card p-4 hover:bg-muted/50" data-testid="travel-alerts">
              <Bell className="h-5 w-5 text-primary" />
              <div className="min-w-0"><p className="text-xs font-semibold text-primary">PRICE ALERTS</p><p className="truncate font-semibold">{watches.length} route{watches.length === 1 ? "" : "s"} watched</p><p className="truncate text-xs text-muted-foreground">Checked daily</p></div>
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FeaturedPlaces() {
  const [d, setD] = useState(null);
  useEffect(() => {
    travelApi.destinations("").then(setD).catch(() => setD(null));
  }, []);
  const items = (d?.featured || []).slice(0, 8);
  if (!items.length) return null;
  return (
    <Section title="Popular from the UK" lede="Real pictures of real places. Pick one to see live fares and things to do.">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {items.map((c) => <PlaceCard key={c.id} c={c} onPick={(x) => { window.location.assign(`/travel/plan?q=${encodeURIComponent(x.name)}`); }} />)}
      </div>
    </Section>
  );
}

function CityPhoto({ c }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    travelApi.photo({ kind: "city", name: c.name, country: c.country, iata: c.iata }).then(setP).catch(() => setP(null));
  }, [c.name, c.country, c.iata]);
  if (!p?.photo?.url) return null;
  return (
    <figure className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="-mt-6 overflow-hidden rounded-3xl"><img src={p.photo.url} alt={`${c.name} — ${p.photo.title || ""}`} className="max-h-[520px] w-full object-cover" /></div>
      <figcaption className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">{p.flag && <img src={p.flag} alt="" className="h-3 w-[18px] rounded-[2px] object-cover" />} {c.name}, {c.country} · Photo: <a href={p.photo.credit_url} target="_blank" rel="noopener noreferrer" className="underline">{p.photo.credit}</a></figcaption>
    </figure>
  );
}

export function CityPage() {
  const { slug } = useParams();
  const c = cityBySlug(slug);
  usePageMeta(c ? `${c.name} travel guide` : "City guide", c ? `${c.name}: when to go, getting in from the airport, where to stay, what to book ahead and local tips. Search flights from UK airports.` : undefined);
  if (!c) return <NotFound />;
  const next = SITE.cities.filter((o) => o.slug !== c.slug).slice(0, 4);
  return (
    <div>
      <PageHeader kicker={<><Link to="/travel" className="hover:underline">Travel</Link> · City guide</>} title={c.name} lede={c.lede}>
        <p className="text-sm text-muted-foreground">Reviewed {SITE.reviewed}. Times and rules change, so check before you travel.</p>
      </PageHeader>
      <CityPhoto c={c} />
      <TripPrepCard city={c.name} country={c.country} />
      <TravelExtras city={c.name} title="Sort the rest of your trip" />
      <Section>
        <div className="grid items-start gap-10 lg:grid-cols-[1fr_400px]">
          <Prose>
            <h2>When to go</h2>
            <p>{c.when}</p>
            <h2>Getting in from the airport</h2>
            <p>{c.airport}</p>
          </Prose>
          <div>
            <h3 className="mb-3 text-lg font-bold">Fly to {c.name}</h3>
            <FlightSearch dest={c} />
          </div>
        </div>
        <h2 className="mt-12 font-serif-display text-2xl font-bold">Where to stay</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {c.areas.map((a) => <InfoCard key={a.name} title={a.name}>{a.body}</InfoCard>)}
        </div>
      </Section>
      <Section alt>
        <div className="grid gap-10 md:grid-cols-2">
          <Prose>
            <h2 style={{ marginTop: 0 }}>What to book ahead</h2>
            <ul>{c.do.map((x) => <li key={x}>{x}</li>)}</ul>
          </Prose>
          <Prose>
            <h2 style={{ marginTop: 0 }}>Local tips</h2>
            <ul>{c.tips.map((x) => <li key={x}>{x}</li>)}</ul>
            <h2>Staying connected</h2>
            <p>Get a {c.country.replace("the ", "")} eSIM before you fly and you'll have data the moment you land. <Link to="/travel/esims">How eSIMs work</Link>.</p>
            <p><OutLink href={airalo(c.esim)}>eSIM for {c.name}</OutLink></p>
          </Prose>
        </div>
        <h2 className="mt-12 font-serif-display text-2xl font-bold">Tickets & tours in {c.name}</h2>
        <div className="mt-4"><ThingsToDo city={c.name} country={c.country} surface="web_cityguide" /></div>
        <p className="mt-8 text-xs text-muted-foreground">
          Some links on this page are affiliate links: if you book through them we may earn a commission, at no extra cost to you.{" "}
          <Link to="/legal/affiliate-disclosure" className="underline">Affiliate disclosure</Link>.
        </p>
      </Section>
      <Section title="More city guides">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {next.map((o) => <CityTile key={o.slug} c={o} />)}
        </div>
      </Section>
    </div>
  );
}
